import {
  APP_INITIALIZER,
  ErrorHandler,
  Injectable,
  LOCALE_ID,
  NgModule,
  isDevMode,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ExtraOptions, PreloadAllModules, Router, RouterModule } from '@angular/router';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';

import { environment } from '@environment/environment';
// import { FuseMockApiModule } from '@fuse/lib/mock-api';
// import { FuseConfigModule } from '@fuse/services/config';
import { GrpcLoggerModule } from '@bp-core/src/lib/modules/grpc-logger/grpc-logger.module';
import { BP_CONFIRMATION_SERVICE, BpGrpcErrorInterceptor } from '@bp-core/src/lib/modules/grpc-error';
import { GrpcMessage } from '@ngx-grpc/common';
import { GRPC_INTERCEPTORS, GrpcCoreModule } from '@ngx-grpc/core';
import * as Sentry from '@sentry/angular';

import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeESCL from '@angular/common/locales/es-CL';
import localeESCLExtra from '@angular/common/locales/extra/es-CL';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';

import { CustomRouter } from '@bp-core/src/lib/services/custom-router';
import { provideFuse } from '@fuse';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';

import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ServiceWorkerModule } from '@angular/service-worker';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { grpc } from '@improbable-eng/grpc-web';
import { provideLumberjack } from '@ngworker/lumberjack';
import { provideLumberjackConsoleDriver } from '@ngworker/lumberjack/console-driver';
import { ImprobableEngGrpcWebClientModule } from '@ngx-grpc/improbable-eng-grpc-web-client';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { ToastrModule } from 'ngx-toastr';
import { Angulartics2Module } from 'angulartics2';
import { AppComponent } from 'app/app.component';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { provideTransloco } from 'app/core/transloco/transloco.provider';
import { LayoutComponent } from 'app/layout/layout.component';
import { bpAppRoutes } from 'bp/app.routes';
import { BpConfirmationService } from 'bp/shared/services/bp-confirmation.service';
import { BpPendingNotificationsService } from 'bp/shared/services/bp-pending-notifications.service';
import { ComponentFactoryService } from 'bp/shared/services/component-factory.service';
import { UserStateService } from 'bp/shared/services/user-state.service';
import { provideClarity } from 'ngx-clarity';
import { MarkdownModule } from 'ngx-markdown';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { GrpcLoadingInterceptor } from 'projects/bp-admin/src/bp/shared/interceptors/grpc-loading.interceptor';
import { BpLoadingInterceptor } from 'projects/bp-admin/src/bp/shared/interceptors/loading.interceptor';
import { OAuthModule } from 'projects/bp-core/src/lib/external/angular-oauth2-oidc-jwks/public_api';
import { AuthGrpcInterceptor } from 'projects/bp-core/src/lib/services/portal/auth-grpc.interceptor';
import { BpHeaderService } from '../bp/shared/services/bp-header.service';
import { mockApiServices } from './mock-api';

const routerConfig: ExtraOptions = {
  preloadingStrategy: PreloadAllModules,
  scrollPositionRestoration: 'enabled',
};
const xhr = grpc.CrossBrowserHttpTransport({});
const ws = grpc.WebsocketTransport();
let portalRoutes = [...bpAppRoutes];

if (environment.production) {
  portalRoutes = [...bpAppRoutes];
}

const providers: any[] = [
  provideClarity({
    enabled: environment.name == 'Production',
    projectId: 'hrh0x7c9ow',
  }),
  { provide: GRPC_INTERCEPTORS, useClass: GrpcLoadingInterceptor, multi: true },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: BpLoadingInterceptor,
    multi: true,
  },
  CustomRouter,
  {
    provide: Router,
    useClass: CustomRouter,
  },
  /*{
    provide: FUSE_CONFIG,
    useValue: appConfig,
  },*/
  {
    provide: LOCALE_ID,
    useValue: 'es-CL',
  },
  {
    provide: GRPC_INTERCEPTORS,
    useClass: AuthGrpcInterceptor,
    multi: true,
  },
  {
    provide: GRPC_INTERCEPTORS,
    useClass: BpGrpcErrorInterceptor,
    multi: true,
  },
  {
    provide: DateAdapter,
    useClass: LuxonDateAdapter,
  },
  {
    provide: MAT_DATE_FORMATS,
    useValue: {
      parse: {
        dateInput: 'D',
      },
      display: {
        dateInput: 'DDD',
        monthYearLabel: 'LLL yyyy',
        dateA11yLabel: 'DD',
        monthYearA11yLabel: 'LLLL yyyy',
      },
    },
  },
  // Monaco Editor config
  {
    provide: APP_INITIALIZER,
    useFactory: () => {
      return () => {
        // Configuración global para Monaco Editor
        (window as any).MonacoEnvironment = {
          getWorkerUrl: function (moduleId: any, label: string) {
            if (label === 'json') {
              return './assets/monaco/json.worker.js';
            }
            if (label === 'css' || label === 'scss' || label === 'less') {
              return './assets/monaco/css.worker.js';
            }
            if (label === 'html' || label === 'handlebars' || label === 'razor') {
              return './assets/monaco/html.worker.js';
            }
            if (label === 'typescript' || label === 'javascript') {
              return './assets/monaco/ts.worker.js';
            }
            return './assets/monaco/editor.worker.js';
          },
        };
      };
    },
    multi: true,
  },

  // Transloco Config
  provideTransloco(),

  // Fuse
  provideAuth(),
  provideIcons(),
  provideFuse({
    mockApi: {
      delay: 0,
      services: mockApiServices,
    },
    fuse: {
      layout: 'classy',
      scheme: 'light',
      screens: {
        sm: '600px',
        md: '960px',
        lg: '1280px',
        xl: '1440px',
      },
      theme: 'theme-bp',
      themes: [
        {
          id: 'theme-bp',
          name: 'Betterplan',
        },
        {
          id: 'theme-default',
          name: 'Default',
        },
        {
          id: 'theme-brand',
          name: 'Brand',
        },
        {
          id: 'theme-teal',
          name: 'Teal',
        },
        {
          id: 'theme-rose',
          name: 'Rose',
        },
        {
          id: 'theme-purple',
          name: 'Purple',
        },
        {
          id: 'theme-amber',
          name: 'Amber',
        },
      ],
    },
  }),
  BpPendingNotificationsService,
  UserStateService,
  ComponentFactoryService,
  BpHeaderService,
  provideLumberjack(),
  provideLumberjackConsoleDriver(),
  // gRPC Error Handling - connects BpErrorHandlerService with BpConfirmationService
  { provide: BP_CONFIRMATION_SERVICE, useExisting: BpConfirmationService },
];

@Injectable()
export class DevelopErrorHandler implements ErrorHandler {
  constructor(private bpConfirmationService: BpConfirmationService) {}
  handleError(error: any) {
    const whiteList = ['Argumento de constructor de matriz con tipo no válido', '_avast_submit'];

    whiteList.forEach(element => {
      if (error.message?.includes(element)) {
        console.error('white-list error:', [error]);
        return;
      }
    });

    console.log(error);

    console.error('dev error handler:', error);

    if (this.bpConfirmationService.openDialogs().length > 0) {
      return;
    }

    this.bpConfirmationService.open(
      {
        title: 'Error no controlado: ' + error.name,
        message: error.message,
      },
      'error',
    );
    /*Swal.fire({
      iconHtml: '<i class="icon icon-swal fas fa-exclamation-triangle"></i>',
      customClass: {
        container: 'swal2-fixe',
        confirmButton: 'btn',
        cancelButton: 'btn',
      },
      buttonsStyling: false,
      showClass: {
        icon: 'swal2-noanimation',
      },
      hideClass: {
        icon: 'swal2-noanimation',
      },
      footer:
        '<div class="border"><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div></div>',
      icon: 'error',
      title: 'Error no controlado:' + error.name,
      text: error.message,
    });*/
    // LogRocket.captureException(error);
  }
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor(private authService: AuthService) {}
  handleError(error: any) {
    const whiteList = ['Argumento de constructor de matriz con tipo no válido', '_avast_submit'];

    whiteList.forEach(element => {
      if (error.message?.includes(element)) {
        console.error('white-list error:', [error]);
        return;
      }
    });

    console.log(error);
    const eventId = Sentry.captureException(error.originalError || error);
    const user = this.authService.userSubject$.value;
    const name = this.authService.userSubject$.value
      ? this.authService.userSubject$.value.firstname +
        ' ' +
        this.authService.userSubject$.value.surname
      : '';
    const username = this.authService.userSubject$.value
      ? this.authService.userSubject$.value.email
      : '';
    // Sentry.configureScope(scope => {
    //   scope.setExtra('sessionURL', LogRocket.sessionURL);
    // });
    let userRaw: any = {};
    if (user) {
      userRaw = user?.toProtobufJSON();
    }
    Sentry.showReportDialog({
      eventId,
      lang: 'es',
      user: {
        ...userRaw,
        name,
        email: username,
      },
      title: 'Parece que estamos teniendo problemas.',
    });
    //LogRocket.captureException(error);
  }
}
if (environment.name == 'Production') {
  datadogRum.init({
    applicationId: '36b5cd75-68f1-443d-8d2a-b2fa3534e7f0',
    clientToken: 'pub6626897b79988b16fa3f885bfe493c1f',
    site: 'us3.datadoghq.com',
    service: 'bp-client-admin',
    env: environment.name.toLowerCase(),
    version: environment.version,
    sessionSampleRate: 20,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Enmascara el texto y los campos de entrada en la grabación de sesión antes de que salga del
    // navegador: el administrador ve datos personales y financieros de clientes (nombre, RUT, correo,
    // teléfono, patrimonio, aportes) y el destino de Datadog está fuera de Chile. Se conserva el
    // recorrido (pantallas, clics, errores, tiempos) y se pierde solo el contenido. Mismo criterio que
    // bp-portal y bp-advisor.
    defaultPrivacyLevel: 'mask',
    // Con `mask`, el nombre de las acciones (clics) también se enmascara: el SDK arma el nombre con
    // el texto del botón o del elemento clicado, que puede traer datos del cliente, y ese nombre viaja
    // aparte de la grabación. Se conserva poniendo `data-dd-action-name` en los botones que importe
    // distinguir en los reportes.
    enablePrivacyForActionName: true,
    allowedTracingUrls: [
      window.location.origin,
      /^https:\/\/[^/]+\.betterplan\.cl\/.*/,
      /^https:\/\/[^/]+\.fintoc\.com\/.*/,
    ],
  });
  datadogLogs.init({
    clientToken: 'pubb6528991197127a7ca3aa83f4b371d4a',
    site: 'us3.datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 20,
    env: environment.name.toLowerCase(),
    version: environment.version,
    service: 'bp-client-admin',
    forwardConsoleLogs: 'all',
  });
  Sentry.init({
    dsn: 'https://6bff7699dc307d943666048296150149@sentry.betterplan.cl/7', // Sentry propio, proyecto de bp-admin/sudo (antes Sentry Cloud)
    debug: !environment.production,
    release: environment.version,
    environment: environment.name,
    // Sentry Logs: el SDK los asocia al replay (pestaña Logs del replay). Solo warn/error de la
    // consola, para acotar el volumen.
    enableLogs: true,
    replaysSessionSampleRate: 0.1, // 10 % de sesiones y traces; errores y replays con error al 100 %
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/[^/]+\.betterplan\.cl\/.*/],

    integrations: [
      // Registers and configures the Tracing integration,
      // which automatically instruments your application to monitor its
      // performance, including custom Angular routing instrumentation
      Sentry.replayIntegration({
        blockAllMedia: false,
        maskAllText: false,
        maskAllInputs: false,
        networkDetailAllowUrls: [/^https:\/\/[^/]+\.betterplan\.cl\/.*/],
        networkRequestHeaders: ['X-Custom-Header'],
        networkResponseHeaders: ['X-Custom-Header'],
      }),
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      Sentry.browserTracingIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
  });
  //LogRocket.init('xc7ami/betterplan-portal-vnext', {
  //release: `${environment.version}-${environment.name}`,
  //});

  providers.push(
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  );
} else {
  providers.push({ provide: ErrorHandler, useClass: DevelopErrorHandler });
}

// @Injectable()
// export class CustomPsmServiceService extends IPasswordStrengthMeterService {
//   score(password: string): number {
//     const passWithTrim = password.trim();

//     if (passWithTrim.length >= 10 && /[A-Z]/.test(passWithTrim) && /\d/.test(passWithTrim)) {
//       return 4;
//     } else if (passWithTrim.length >= 8 && /[A-Z]/.test(passWithTrim) && /\d/.test(passWithTrim)) {
//       return 3;
//     } else if (passWithTrim.length >= 8 && /[A-Z]/.test(passWithTrim)) {
//       return 2;
//     } else if (passWithTrim.length >= 8) {
//       return 1;
//     } else {
//       return 0;
//     }
//   }

//   // example code for avoid the no-implement error
//   scoreWithFeedback(
//     password: string,
//   ): {
//     score: number;
//     feedback: { warning: string; suggestions: string[] };
//   } {
//     return { score: 1, feedback: { warning: '', suggestions: [] } };
//   }
// }

registerLocaleData(localeESCL, localeESCLExtra);

export const customCurrencyMaskConfig = {
  align: 'left',
  allowNegative: false,
  allowZero: false,
  decimal: ',',
  precision: 0,
  prefix: '$',
  suffix: '',
  thousands: '.',
  nullable: true,
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    SweetAlert2Module.forRoot(),
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    // AppInsightsModule.forRoot({
    //   instrumentationKey: environment.appInsightsInstrumentationKey,
    // }),
    BrowserAnimationsModule,
    RouterModule.forRoot(portalRoutes, routerConfig),

    // Fuse, FuseConfig & FuseMockAPI
    // FuseConfigModule.forRoot(appConfig),
    // FuseMockApiModule.forRoot(mockApiServices),

    // Core module of your application

    // Layout module of your application
    LayoutComponent,

    // 3rd party modules that require global configuration via forRoot
    Angulartics2Module.forRoot(),
    OAuthModule.forRoot(),
    GoogleTagManagerModule.forRoot({
      id: 'GTM-N3S8WSB',
    }),
    MatSnackBarModule,
    MarkdownModule.forRoot({}),
    GrpcCoreModule.forRoot(),
    MonacoEditorModule.forRoot(), // Agregamos la importación del módulo Monaco
    // GrpcWebClientModule.forRoot({
    //   settings: {
    //     host: environment.urlGrpc,
    //     format: 'binary',
    //     withCredentials: false,
    //     suppressCorsPreflight: false,
    //   },
    // }),
    ImprobableEngGrpcWebClientModule.forChild({
      settings: {
        host: environment.urlGrpc,
        // we might want to use different transports as recommended by improbable-eng team
        // because websocket transport acts a bit differently and is intended for client streaming only
        transport: {
          unary: xhr,
          serverStream: xhr,
          clientStream: ws,
          bidiStream: ws,
        },
        // or simply e.g.
        // transport: ws, // to configure all methods to use websockets
      },
    }),
    GrpcLoggerModule.forRoot({
      settings: {
        // enables logger in dev mode and still lets you see them in production when running `localStorage.setItem('logger', 'true') in the console`
        // enabled:
        //   localStorage.getItem('logger') === 'true' || !environment.production,
        enabled: true,
        // protobuf json is more human-readable than the default toObject() mapping
        // please beware: if you use google.protobuf.Any you must pass the proper `messagePool` argument
        requestMapper: (msg: GrpcMessage) => msg.toProtobufJSON(),
        responseMapper: (msg: GrpcMessage) => msg.toProtobufJSON(),
      },
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers,
  bootstrap: [AppComponent],
})
export class AppModule {}
